// ============================================
// Color Palette for Dashboard
// ============================================

export const colors = {
  // Semantic colors
  positive: '#10B981', // Green - Success, growth
  negative: '#EF4444', // Red - Error, decline
  neutral: '#6B7280', // Gray - No change
  warning: '#F59E0B', // Amber - Caution
  info: '#3B82F6', // Blue - Information

  // Platform colors
  facebook: '#1877F2',
  google: '#4285F4',
  instagram: '#E4405F',
  linkedin: '#0A66C2',

  // Chart series colors (max 6)
  series: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'],

  // Chart series with opacity variants
  seriesLight: [
    'rgba(59, 130, 246, 0.1)',
    'rgba(16, 185, 129, 0.1)',
    'rgba(245, 158, 11, 0.1)',
    'rgba(239, 68, 68, 0.1)',
    'rgba(139, 92, 246, 0.1)',
    'rgba(236, 72, 153, 0.1)',
  ],

  // Background colors
  background: {
    primary: '#FFFFFF',
    secondary: '#F9FAFB',
    tertiary: '#F3F4F6',
  },

  // Border colors
  border: {
    light: '#E5E7EB',
    medium: '#D1D5DB',
    dark: '#9CA3AF',
  },

  // Text colors
  text: {
    primary: '#111827',
    secondary: '#6B7280',
    tertiary: '#9CA3AF',
    inverse: '#FFFFFF',
  },
} as const;

/**
 * Get trend color based on direction and metric type
 */
export function getTrendColor(trend: 'up' | 'down' | 'flat', metricType: 'growth' | 'cost' = 'growth'): string {
  if (trend === 'flat') return colors.neutral;

  if (metricType === 'cost') {
    // For cost metrics, down is good
    return trend === 'down' ? colors.positive : colors.negative;
  }

  // For growth metrics, up is good
  return trend === 'up' ? colors.positive : colors.negative;
}

/**
 * Get platform color
 */
export function getPlatformColor(platform: string): string {
  const platformMap: Record<string, string> = {
    facebook: colors.facebook,
    google_ads: colors.google,
    google: colors.google,
    instagram: colors.instagram,
    linkedin: colors.linkedin,
  };

  return platformMap[platform.toLowerCase()] || colors.series[0];
}

/**
 * Get color by index for charts
 */
export function getSeriesColor(index: number): string {
  return colors.series[index % colors.series.length];
}

/**
 * Get light variant of series color
 */
export function getSeriesColorLight(index: number): string {
  return colors.seriesLight[index % colors.seriesLight.length];
}

/**
 * Generate gradient for area charts
 */
export function getGradient(ctx: CanvasRenderingContext2D, color: string): CanvasGradient {
  const gradient = ctx.createLinearGradient(0, 0, 0, 300);
  gradient.addColorStop(0, color.replace(')', ', 0.4)').replace('rgb', 'rgba'));
  gradient.addColorStop(1, color.replace(')', ', 0)').replace('rgb', 'rgba'));
  return gradient;
}
